import { BusinessObject, ValidateAndFixResult } from './business-object.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import type { TsCodeType } from '../types.js'
import { BoVisitor } from './bo-visitor.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import { BoReference } from './bo-reference.js'

export const TriggerTypes = {
	manual: 'Manual',
	timer: 'Timer (cron job)',
	mail: 'Incoming mail',
	file: 'File (watch folder)',
	entityChange: 'Entity change on DB',
} as const
export type TriggerType = keyof typeof TriggerTypes

export const TriggerEntityChangeChangeTypes = {
	insert: 'Insert',
	update: 'Update',
	delete: 'Delete',
} as const
export type TriggerEntityChangeChangeType =
	keyof typeof TriggerEntityChangeChangeTypes

export const TriggerEntityChangeTimings = {
	before:
		'Before writing to DB. Allows to modify the entity before it is written to DB or to cancel the operation by throwing an error',
	after:
		'After writing to DB. Allows to perform additional operations after the entity has been written to DB',
	asyncAfter:
		'Async after writing to DB. Schedules additional operations to be performed separately after the entity has been written to DB. Does not have access to old value!',
} as const
export type TriggerEntityChangeTiming = keyof typeof TriggerEntityChangeTimings

@jsonClass('Trigger')
export class Trigger extends BusinessObject {
	@jsonProperty(String)
	triggerType: TriggerType = 'manual'
	@jsonProperty(String)
	tsCode: TsCodeType = ''
	@jsonProperty(Number)
	order: number = 100

	@jsonArrayProperty(String)
	timerCronExpressions: string[] = []

	@jsonProperty(BoReference)
	mailConfigRef?: BoReference<'ConfigurationItem'>
	@jsonProperty(String)
	mailFolderName: string = 'INBOX'
	@jsonProperty(Boolean)
	mailIgnoreExisting: boolean = false
	@jsonProperty(Boolean)
	mailIgnoreRead: boolean = true
	@jsonProperty(Boolean)
	mailMarkAsRead: boolean = true
	@jsonProperty(String)
	mailMoveToFolderName?: string
	// @jsonProperty(String)
	// mailCriteriaCode: string = `['ALL']`
	@jsonProperty(String)
	mailFilterCode?: string

	@jsonArrayProperty(String)
	fileWatchGlobExpressions: string[] = []
	@jsonProperty(Boolean)
	fileWatchIgnoreInitial: boolean = true

	@jsonArrayProperty(BoReference)
	entityChangeEntityRefs: BoReference<'Entity'>[] = []
	@jsonArrayProperty(String)
	entityChangeChangeTypes: TriggerEntityChangeChangeType[] = []
	@jsonProperty(String)
	entityChangeTiming: TriggerEntityChangeTiming = 'asyncAfter'
	get entityChangeEntityTsType() {
		return this.entityChangeEntityRefs.length
			? this.entityChangeEntityRefs
					.map((ref) => ref.getQualifiedName())
					.join(' | ')
			: 'never'
	}
	get entityChangeEntityChangeTsType() {
		return this.entityChangeChangeTypes.length
			? this.entityChangeChangeTypes.map((t) => `'${t}'`).join(' | ')
			: 'never'
	}

	constructor(init?: Partial<Trigger>) {
		super()
		assignCommonProperties(this, init)
	}

	getClassNames(): string[] {
		return [this.getQualifiedName()]
	}

	validateAndFix(): ValidateAndFixResult {
		if (this.triggerType != 'timer') {
			this.timerCronExpressions = []
		}
		if (this.triggerType != 'file') {
			this.fileWatchGlobExpressions = []
			this.fileWatchIgnoreInitial = true
		}
		return {}
	}

	visit(visitor: BoVisitor): void {
		visitor.visitTsCode(
			this.tsCode,
			this,
			'serverCodeReturningVoid',
			['tsCode'],
			(newCode) => (this.tsCode = newCode),
		)
	}
}
